import { NgModule, ErrorHandler, APP_INITIALIZER, Provider, Injectable, Inject } from '@angular/core';
import { DatePipe } from '@angular/common';
import { HttpClient, HTTP_INTERCEPTORS } from '@angular/common/http';
import { AuthenticationGuard, CanDeactivateGuard } from './';
import { MsalModule, MsalService, MSAL_INSTANCE, MsalGuardConfiguration, MSAL_GUARD_CONFIG, MsalGuard } from '@azure/msal-angular';
import { AccountInfo, BrowserAuthError, BrowserCacheLocation, InteractionType, LogLevel, PublicClientApplication, IPublicClientApplication } from '@azure/msal-browser';
import { L10nIntlModule, L10nTranslationModule } from 'angular-l10n';
import { MapperService } from './mapper.service';
import { NotificationService } from './notification.service';
import { SamplicityHttpClient } from 'shared/http-client';
import { GlobalErrorHandler } from 'shared/global-error-handler';
import { ServiceBaseConfig } from '../../swagger/service.base';
import { Tenant, TENANT_API_BASE_URL } from 'shared/service-clients/tenant';
import { System, System_API_BASE_URL } from 'shared/service-clients/system';
import { Classification, CLASSIFICATION_API_BASE_URL } from 'shared/service-clients/classification';
import { Localization, LOCALIZATION_API_BASE_URL } from 'shared/service-clients/localization';
import { OrderManagementOrder, OM_ORDER_API_BASE_URL } from 'shared/service-clients/om-order';
import { OrderManagementBatch, OM_BATCH_API_BASE_URL } from 'shared/service-clients/om-batch';
import { OrderManagementOffer, OM_OFFER_API_BASE_URL } from 'shared/service-clients/om-offer';
import { OrderManagementHcp, OM_HCP_API_BASE_URL } from 'shared/service-clients/om-hcp';
import { OrderManagementProduct, OM_PRODUCT_API_BASE_URL } from 'shared/service-clients/om-product';
import { OrderManagementClient, OM_CLIENT_API_BASE_URL } from 'shared/service-clients/om-client';
import { OrderManagementProgram, OM_PROGRAM_API_BASE_URL } from 'shared/service-clients/om-program';
import { OrderManagementIntegration, OM_INTEGRATION_API_BASE_URL } from 'shared/service-clients/om-integration';
import { OrderManagementDashboard, OM_DASHBOARD_API_BASE_URL } from 'shared/service-clients/om-dashboard';
import { Reference, REFERENCE_API_BASE_URL } from 'shared/service-clients/reference';
import { Security, SECURITY_API_BASE_URL } from 'shared/service-clients/security';
import { AuthService } from 'core/auth.service';
import { SecurityService } from 'core/security.service';
import { SystemService } from 'core/system.service';
import { TranslationHelperService } from 'core/translation-helper.service';
import { HelpersService } from 'core/helpers.service';
import { ClientService } from 'core/client.service';
import { BatchService } from 'core/batch.service';
import { ClassificationService } from 'core/classification.service';
import { ReferenceDataService } from 'core/reference-data.service';
import { FilterService } from './filter.service';
import { Communication, Communication_API_BASE_URL } from '../shared/service-clients/communication';
import { TimerService } from 'shared/services/timer.service';
import { ApplicationInsightsService } from './applications-insights.service';
import { DateHelperService } from '../shared/services/date-helper.service';
import { ProgramUtilityService } from 'core/ProgramUtilitySerivce'
import { OM_PHARMACIST_API_BASE_URL, OrderManagementPharmacist } from 'shared/service-clients/om-pharmacist';
import { FreeGoodsUtilityService } from './FreeGoodsUtilityService';
import { OM_CERTIFICATE_API_BASE_URL, OrderManagementCertificate } from 'shared/service-clients/om-certification';
import { OM_CUSTOMER_API_BASE_URL, OrderManagementCustomer } from 'shared/service-clients/om-customer';
import { CustomerService } from './customer.service';
import { EnvService } from '../env/env.service';
import { OrderManagementInvoice } from 'shared/service-clients/om-invoice';
import { L10nTranslationService } from 'angular-l10n';
import { l10nLangConfig } from '../shared/l10n-config';
import { L10nLoader } from 'angular-l10n';
import { SamplicityTranslationLoader } from './translation-service';
const environmentService = new EnvService();

export function getClassificationServiceUrl(): string {
  return environmentService.get().classificationServiceUrl;
}
export function getLocalizationServiceUrl(): string {
  return environmentService.get().localizationServiceUrl;
}
export function getOrderManagementServiceUrl(): string {
  return environmentService.get().orderManagementServiceUrl;
}
export function getAADClientId(): string {
  return environmentService.get().aadClientId;
}
export function getAADTenantId(): string {
  return environmentService.get().aadTenantId;
}
export function getMSAL_SCOPES() {
  return {
    apiRead: `api://${getAADClientId()}/api.read`,
    userRead: 'user.read'
  };
}
export function getReferenceDataServiceUrl(): string {
  return environmentService.get().referenceDataServiceUrl;
}
export function getSecurityServiceUrl(): string {
  return environmentService.get().securityServiceUrl;
}
export function getTenantServiceUrl(): string {
  return environmentService.get().tenantServiceUrl;
}
export function getSystemServiceUrl(): string {
  return environmentService.get().systemServiceUrl;
}
export function getCommunicationServiceUrl(): string {
  return environmentService.get().communicationServiceUrl;
}
export function getServiceBaseConfig(): ServiceBaseConfig {
  const serviceBaseConfig = new ServiceBaseConfig();
  return serviceBaseConfig;
}
export function classificationServiceClientFactory(http: HttpClient): Classification {
  return new Classification(getServiceBaseConfig(), http, getClassificationServiceUrl());
}
export function localizationServiceClientFactory(http: HttpClient): Localization {
  return new Localization(getServiceBaseConfig(), http, getLocalizationServiceUrl());
}
export function omOrderServiceClientFactory(http: HttpClient): OrderManagementOrder {
  return new OrderManagementOrder(getServiceBaseConfig(), http, getOrderManagementServiceUrl());
}
export function omBatchServiceClientFactory(http: HttpClient): OrderManagementBatch {
  return new OrderManagementBatch(getServiceBaseConfig(), http, getOrderManagementServiceUrl());
}
export function omOfferServiceClientFactory(http: HttpClient): OrderManagementOffer {
  return new OrderManagementOffer(getServiceBaseConfig(), http, getOrderManagementServiceUrl());
}
export function omHcpServiceClientFactory(http: HttpClient): OrderManagementHcp {
  return new OrderManagementHcp(getServiceBaseConfig(), http, getOrderManagementServiceUrl());
}
export function omPharmacistServiceClientFactory(http: HttpClient): OrderManagementPharmacist {
  return new OrderManagementPharmacist(getServiceBaseConfig(), http, getOrderManagementServiceUrl());
}
export function omProductServiceClientFactory(http: HttpClient): OrderManagementProduct {
  return new OrderManagementProduct(getServiceBaseConfig(), http, getOrderManagementServiceUrl());
}
export function omClientServiceClientFactory(http: HttpClient): OrderManagementClient {
  return new OrderManagementClient(getServiceBaseConfig(), http, getOrderManagementServiceUrl());
}
export function omProgramServiceClientFactory(http: HttpClient): OrderManagementProgram {
  return new OrderManagementProgram(getServiceBaseConfig(), http, getOrderManagementServiceUrl());
}
export function omIntegrationServiceClientFactory(http: HttpClient): OrderManagementIntegration {
  return new OrderManagementIntegration(getServiceBaseConfig(), http, getOrderManagementServiceUrl());
}
export function omDashboardServiceClientFactory(http: HttpClient): OrderManagementDashboard {
  return new OrderManagementDashboard(getServiceBaseConfig(), http, getOrderManagementServiceUrl());
}
export function referenceServiceClientFactory(http: HttpClient): Reference {
  return new Reference(getServiceBaseConfig(), http, getReferenceDataServiceUrl());
}
export function securityServiceClientFactory(http: HttpClient): Security {
  return new Security(getServiceBaseConfig(), http, getSecurityServiceUrl());
}
export function tenantServiceClientFactory(http: HttpClient): Tenant {
  return new Tenant(getServiceBaseConfig(), http, getTenantServiceUrl());
}
export function systemServiceClientFactory(http: HttpClient): System {
  return new System(getServiceBaseConfig(), http, getSystemServiceUrl());
}
export function communicationServiceClientFactory(http: HttpClient): Communication {
  return new Communication(getServiceBaseConfig(), http, getCommunicationServiceUrl());
}
export function omCertificationServiceClientFactory(http: HttpClient): OrderManagementCertificate {
  return new OrderManagementCertificate(getServiceBaseConfig(), http, getOrderManagementServiceUrl());
}
export function omCustomerServiceClientFactory(http: HttpClient): OrderManagementCustomer {
  return new OrderManagementCustomer(getServiceBaseConfig(), http, getOrderManagementServiceUrl());
}
export function omInvoiceClientFactory(http: HttpClient): OrderManagementInvoice {
  return new OrderManagementInvoice(getServiceBaseConfig(), http, getOrderManagementServiceUrl());
}

export const msalInstance: IPublicClientApplication = new PublicClientApplication({
  auth: {
    clientId: getAADClientId(),
    authority: `https://login.microsoftonline.com/${getAADTenantId()}`,
    redirectUri: '/',
    postLogoutRedirectUri: '/'
  },
  cache: {
    cacheLocation: BrowserCacheLocation.LocalStorage,
    storeAuthStateInCookie: true
  },
  system: {
    loggerOptions: {
      logLevel: LogLevel.Info,
      piiLoggingEnabled: false
    }
  }
});

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    loginFailedRoute: `/login-failed`
  }
}

export function setAccountToken(accessToken: string, account: AccountInfo, msal: MsalService): void {
  const idTokenKey: string = 'samplicity-id-token';
  const accessTokenKey: string = 'samplicity-access-token';

  msal.instance.setActiveAccount(account);
  localStorage.setItem(idTokenKey, accessToken);
  localStorage.setItem(accessTokenKey, accessToken);
}

export function initLocalization(l10nLoader: L10nLoader): () => Promise<void> {
  return () => l10nLoader.init();
}

export function initializeMSAL(msal: MsalService, l10nLoader: L10nLoader, translation: L10nTranslationService): Promise<any> {
  return new Promise((resolve) => {
    msal.instance.handleRedirectPromise().then((response) => {
      if (response != null) {
        setAccountToken(response.accessToken, response.account, msal);
        resolve(true);

      } else {
        const silentTokenRequest = {
          scopes: [getMSAL_SCOPES().apiRead]
        }
        return msal.acquireTokenSilent(silentTokenRequest).subscribe(tokenResponse => {
          setAccountToken(tokenResponse.accessToken, tokenResponse.account, msal);
          resolve(true);
        }, (error) => {
          if (error instanceof BrowserAuthError) {
            msal.loginRedirect(silentTokenRequest);
          }
        });
      }
    });
  });
}

export function initializeApp(msal: MsalService, l10nLoader: L10nLoader, translation: L10nTranslationService) {
  return () => {
    return initializeMSAL(msal, l10nLoader, translation).then(() => initLocalization(l10nLoader));
  }
}

@NgModule({
  imports: [
    MsalModule,
    L10nTranslationModule.forRoot(l10nLangConfig, {
      translationLoader: SamplicityTranslationLoader
    }),
    L10nIntlModule,
  ],
  declarations: [],
  providers: [
    AuthenticationGuard,
    AuthService,
    SecurityService,
    SystemService,
    CanDeactivateGuard,
    ServiceBaseConfig,
    {
      provide: MSAL_INSTANCE,
      useValue: msalInstance
    },
    MsalService,
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApp,
      deps: [MsalService, L10nLoader, L10nTranslationService],
      multi: true
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory
    },
    MsalGuard,
    Tenant,
    System,
    Localization,
    Security,
    MapperService,
    NotificationService,
    TranslationHelperService,
    ProgramUtilityService,
    HelpersService,
    FilterService,
    ClientService,
    BatchService,
    CustomerService,
    ClassificationService,
    ReferenceDataService,
    TimerService,
    DatePipe,
    DateHelperService,
    ApplicationInsightsService,
    FreeGoodsUtilityService,
    { provide: CLASSIFICATION_API_BASE_URL, useFactory: getClassificationServiceUrl },
    {
      provide: Classification,
      useFactory: classificationServiceClientFactory,
      deps: [HttpClient]
    },
    { provide: LOCALIZATION_API_BASE_URL, useFactory: getLocalizationServiceUrl },
    {
      provide: Localization,
      useFactory: localizationServiceClientFactory,
      deps: [HttpClient]
    },
    { provide: OM_ORDER_API_BASE_URL, useFactory: getOrderManagementServiceUrl },
    {
      provide: OrderManagementOrder,
      useFactory: omOrderServiceClientFactory,
      deps: [HttpClient]
    },
    { provide: OM_BATCH_API_BASE_URL, useFactory: getOrderManagementServiceUrl },
    {
      provide: OrderManagementBatch,
      useFactory: omBatchServiceClientFactory,
      deps: [HttpClient]
    },
    { provide: OM_OFFER_API_BASE_URL, useFactory: getOrderManagementServiceUrl },
    {
      provide: OrderManagementOffer,
      useFactory: omOfferServiceClientFactory,
      deps: [HttpClient]
    },
    { provide: OM_HCP_API_BASE_URL, useFactory: getOrderManagementServiceUrl },
    {
      provide: OrderManagementHcp,
      useFactory: omHcpServiceClientFactory,
      deps: [HttpClient]
    },
    { provide: OM_PHARMACIST_API_BASE_URL, useFactory: getOrderManagementServiceUrl },
    {
      provide: OrderManagementPharmacist,
      useFactory: omPharmacistServiceClientFactory,
      deps: [HttpClient]
    },
    { provide: OM_CERTIFICATE_API_BASE_URL, useFactory: getOrderManagementServiceUrl },
    {
      provide: OrderManagementCertificate,
      useFactory: omCertificationServiceClientFactory,
      deps: [HttpClient]
    },
    { provide: OM_PRODUCT_API_BASE_URL, useFactory: getOrderManagementServiceUrl },
    {
      provide: OrderManagementProduct,
      useFactory: omProductServiceClientFactory,
      deps: [HttpClient]
    },
    { provide: OM_CLIENT_API_BASE_URL, useFactory: getOrderManagementServiceUrl },
    {
      provide: OrderManagementClient,
      useFactory: omClientServiceClientFactory,
      deps: [HttpClient]
    },
    { provide: OM_PROGRAM_API_BASE_URL, useFactory: getOrderManagementServiceUrl },
    {
      provide: OrderManagementProgram,
      useFactory: omProgramServiceClientFactory,
      deps: [HttpClient]
    },
    { provide: OM_INTEGRATION_API_BASE_URL, useFactory: getOrderManagementServiceUrl },
    {
      provide: OrderManagementIntegration,
      useFactory: omIntegrationServiceClientFactory,
      deps: [HttpClient]
    },
    { provide: OM_DASHBOARD_API_BASE_URL, useFactory: getOrderManagementServiceUrl },
    {
      provide: OrderManagementDashboard,
      useFactory: omDashboardServiceClientFactory,
      deps: [HttpClient]
    },
    { provide: REFERENCE_API_BASE_URL, useFactory: getReferenceDataServiceUrl },
    {
      provide: Reference,
      useFactory: referenceServiceClientFactory,
      deps: [HttpClient]
    },
    { provide: SECURITY_API_BASE_URL, useFactory: getSecurityServiceUrl },
    {
      provide: Security,
      useFactory: securityServiceClientFactory,
      deps: [HttpClient]
    },
    { provide: TENANT_API_BASE_URL, useFactory: getTenantServiceUrl },
    {
      provide: Tenant,
      useFactory: tenantServiceClientFactory,
      deps: [HttpClient]
    },
    { provide: System_API_BASE_URL, useFactory: getSystemServiceUrl },
    {
      provide: System,
      useFactory: systemServiceClientFactory,
      deps: [HttpClient]
    },
    { provide: Communication_API_BASE_URL, useFactory: getCommunicationServiceUrl },
    {
      provide: Communication,
      useFactory: communicationServiceClientFactory,
      deps: [HttpClient]
    },
    { provide: OM_CUSTOMER_API_BASE_URL, useFactory: getOrderManagementServiceUrl },
    {
      provide: OrderManagementCustomer,
      useFactory: omCustomerServiceClientFactory,
      deps: [HttpClient]
    },
    { provide: OM_ORDER_API_BASE_URL, useFactory: getOrderManagementServiceUrl },
    {
      provide: OrderManagementInvoice,
      useFactory: omInvoiceClientFactory,
      deps: [HttpClient]
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: SamplicityHttpClient,
      multi: true
    },
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler
    }
  ]
})
export class CoreModule {
  constructor() {
  }
}